import { isHeicFile, convertHeicToJpeg } from "@/lib/heic-conversion";

const MAX_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
]);

export type UploadStage = "validating" | "converting" | "requesting" | "uploading" | "finalizing";

export interface UploadCallbacks {
  onStageChange?: (stage: UploadStage) => void;
  onProgress?: (percent: number) => void;
}

function putFileWithProgress(
  url: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    xhr.setRequestHeader("Content-Type", file.type);
    if (xhr.upload && onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Failed to upload file to storage (${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error("Failed to upload file to storage"));
    xhr.onabort = () => reject(new Error("Upload aborted"));
    xhr.send(file);
  });
}

export function useFileUpload() {
  const uploadFile = async (
    fileInput: File,
    visibility: "public" | "private" = "private",
    callbacks: UploadCallbacks = {},
  ) => {
    const BASE = import.meta.env.BASE_URL;
    const { onStageChange, onProgress } = callbacks;

    onStageChange?.("validating");
    if (fileInput.size > MAX_UPLOAD_SIZE_BYTES) {
      throw new Error(
        `File too large. Maximum upload size is ${MAX_UPLOAD_SIZE_BYTES / (1024 * 1024)} MB.`,
      );
    }

    // Transparently convert HEIC/HEIF (iPhone default camera format) to
    // JPEG before any further validation. Browsers can't render HEIC in
    // <img>, sharp can't decode it on the server, and the storage finalize
    // route rejects ISO BMFF bytes, so the only way uploads "just work" for
    // iPhone users is to convert client-side.
    let file = fileInput;
    if (await isHeicFile(file)) {
      onStageChange?.("converting");
      try {
        file = await convertHeicToJpeg(file);
      } catch (err) {
        const detail = err instanceof Error ? err.message : String(err);
        throw new Error(
          `Couldn't convert this HEIC photo to JPEG. ${detail}. Try opening the photo in your Photos app and exporting it as JPEG, then re-upload.`,
        );
      }
    }

    if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
      throw new Error(
        "Unsupported file type. Only JPEG, PNG, WebP, and GIF images are allowed.",
      );
    }
    // Re-check size after conversion — converted JPEGs are usually smaller
    // than the HEIC source, but defend against the edge case.
    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      throw new Error(
        `File too large after converting from HEIC. Maximum upload size is ${MAX_UPLOAD_SIZE_BYTES / (1024 * 1024)} MB.`,
      );
    }

    onStageChange?.("requesting");
    const requestRes = await fetch(`${BASE}api/storage/uploads/request-url`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "x-requested-with": "XMLHttpRequest",
      },
      body: JSON.stringify({ name: file.name, size: file.size, contentType: file.type }),
    });

    if (!requestRes.ok) {
      const errData = await requestRes.json().catch(() => ({}));
      throw new Error(errData.error || `Upload request failed (${requestRes.status})`);
    }

    const { uploadURL, objectPath } = await requestRes.json();

    onStageChange?.("uploading");
    onProgress?.(0);
    await putFileWithProgress(uploadURL, file, onProgress);
    onProgress?.(100);

    onStageChange?.("finalizing");
    const finalizeRes = await fetch(`${BASE}api/storage/uploads/finalize`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "x-requested-with": "XMLHttpRequest",
      },
      body: JSON.stringify({ objectPath, visibility }),
    });

    if (!finalizeRes.ok) {
      const errData = await finalizeRes.json().catch(() => ({}));
      throw new Error(errData.error || `Finalize failed (${finalizeRes.status})`);
    }

    if (file.type.startsWith("image/") && objectPath?.startsWith("/objects/")) {
      const cleanPath = objectPath.slice("/objects/".length);
      const warmUrl = `${BASE}api/storage/objects/${cleanPath}?w=400&v=2`;
      void fetch(warmUrl, { credentials: "include" }).catch(() => {});
    }

    return objectPath;
  };

  return { uploadFile };
}
